import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Headset } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import UserManagementTab from "@/components/admin/UserManagementTab";
import LazyTabContent from "@/components/admin/LazyTabContent";
import ContactIngantiSupportDialog from "@/components/ContactIngantiSupportDialog";
import useDebounce from "@/hooks/useDebounce";

// Lazy load tab components
const HRTab = React.lazy(() => import("@/components/admin/HRTab"));
const SalesTab = React.lazy(() => import("@/components/admin/SalesTab"));
const MarketingTab = React.lazy(() => import("@/components/admin/MarketingTab"));
const FinanceTab = React.lazy(() => import("@/components/admin/FinanceTab"));
const ProjectsTab = React.lazy(() => import("@/components/admin/ProjectsTab"));
const PerformanceTab = React.lazy(() => import("@/components/admin/PerformanceTab"));
const LegalTab = React.lazy(() => import("@/components/admin/LegalTab"));

const Administration = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("user-management");
  const [showContactDialog, setShowContactDialog] = useState(false);
  
  // Debounce active tab to prevent rapid switching
  const debouncedActiveTab = useDebounce(activeTab, 150);
  
  // HR Tab data - only load when HR tab is active
  const [projectRequirements, setProjectRequirements] = useState<any[]>([]);
  const [organizationStructures, setOrganizationStructures] = useState<any[]>([]);
  const [nodes, setNodes] = useState<any[]>([]);
  const [roleJds, setRoleJds] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string>("");
  const [hrDataLoading, setHrDataLoading] = useState(false);
  
  useEffect(() => {
    const checkSession = async () => {
      try {
        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          throw sessionError;
        }
        
        if (!session) {
          toast.error("Please sign in to access the administration module");
          navigate('/');
          return;
        }

        // Verify the user is an admin before granting access
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('is_admin, access_administration')
          .eq('id', session.user.id)
          .single();

        if (profileError || !profile?.is_admin || !profile?.access_administration) {
          toast.error("You do not have permission to access the administration module");
          navigate('/');
          return;
        }

        setUser(session.user);
      } catch (error) {
        console.error('Authentication error:', error);
        toast.error("Session expired. Please sign in again.");
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        navigate('/');
        return;
      }
      if (session?.user) {
        setUser(session.user);
      }
    });

    checkSession();
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Lazy load HR data only when HR tab is active
  useEffect(() => {
    if (debouncedActiveTab !== 'hr') return;
    
    const fetchProjectRequirements = async () => {
      setHrDataLoading(true);
      try {
        const { data, error } = await supabase
          .from('project_requirements')
          .select('*')
          .order('name', { ascending: true });
        
        if (error) throw error;
        setProjectRequirements(data || []);
      } catch (error) {
        console.error('Error fetching project requirements:', error);
        toast.error("Failed to fetch project requirements");
      } finally {
        setHrDataLoading(false);
      }
    };

    fetchProjectRequirements();
  }, [debouncedActiveTab]);

  // Load related HR data when project is selected
  useEffect(() => {
    if (debouncedActiveTab !== 'hr' || !selectedProjectId) {
      setOrganizationStructures([]);
      setNodes([]);
      setRoleJds([]);
      setCandidates([]);
      setSelectedProjectName("");
      return;
    }

    const selectedProject = projectRequirements.find(pr => pr.project_id === selectedProjectId);
    if (selectedProject) {
      setSelectedProjectName(selectedProject.name);
    }

    const fetchRelatedData = async () => {
      setHrDataLoading(true);
      try {
        // Reset data
        setOrganizationStructures([]);
        setNodes([]);
        setRoleJds([]);
        setCandidates([]);

        // Fetch all related data in parallel
        const [orgStructures, nodeData, roleData, candidateData] = await Promise.all([
          supabase.from('organization_structures').select('*').eq('project_id', selectedProjectId),
          supabase.from('nodes').select('*').eq('project_id', selectedProjectId).order('node_type', { ascending: true }),
          supabase.from('role_jd').select('*').eq('project_id', selectedProjectId).order('role_title', { ascending: true }),
          supabase.from('candidates').select('*').eq('project_id', selectedProjectId)
        ]);

        if (orgStructures.error) throw orgStructures.error;
        if (nodeData.error) throw nodeData.error;
        if (roleData.error) throw roleData.error;
        if (candidateData.error) throw candidateData.error;

        setOrganizationStructures(orgStructures.data || []);
        setNodes(nodeData.data || []);
        setRoleJds(roleData.data || []);

        // Transform and sort candidates
        const transformedCandidates = (candidateData.data || []).map(candidate => ({
          ...candidate,
          candidate_position: `${candidate.candidate_id} of ${candidate.number_of_candidates || 1}`
        }));

        const sortedCandidates = [...transformedCandidates].sort((a, b) => {
          const roleComparison = a.role_title.localeCompare(b.role_title);
          if (roleComparison !== 0) return roleComparison;
          return a.candidate_id - b.candidate_id;
        });

        setCandidates(sortedCandidates);
      } catch (error) {
        console.error('Error fetching related data:', error);
        toast.error("Failed to fetch related data");
      } finally {
        setHrDataLoading(false);
      }
    };

    fetchRelatedData();
  }, [selectedProjectId, projectRequirements, debouncedActiveTab]);

  if (isLoading) {
    return null;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-secondary">
      <TopFrame user={user} hideProgress={true} />
      <main className="max-w-7xl mx-auto px-4">
        <div className="mt-8 space-y-4">
          <div className="relative flex items-center justify-between">
            <p className="text-sm text-white">
              (Tables below are read-only and database administration is offered as a managed service)
            </p>
            <Button 
              variant="outline" 
              className="bg-primary/10 border-primary/20 hover:bg-transparent text-white"
              onClick={() => setShowContactDialog(true)}
            >
              <Headset className="mr-2" />
              Contact Support
            </Button>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-center bg-secondary/50 border border-primary/20">
              <TabsTrigger value="user-management" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                User Management
              </TabsTrigger>
              <TabsTrigger value="performance" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                Performance
              </TabsTrigger>
              <TabsTrigger value="projects" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                Projects
              </TabsTrigger>
              <TabsTrigger value="hr" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                HR
              </TabsTrigger>
              <TabsTrigger value="sales" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                Sales
              </TabsTrigger>
              <TabsTrigger value="marketing" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                Marketing
              </TabsTrigger>
              <TabsTrigger value="finance" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                Finance
              </TabsTrigger>
              <TabsTrigger value="legal" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black">
                Legal
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="user-management">
              <UserManagementTab />
            </TabsContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="performance">
              <TabsContent value="performance">
                <PerformanceTab user={user} />
              </TabsContent>
            </LazyTabContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="projects">
              <TabsContent value="projects">
                <ProjectsTab user={user} />
              </TabsContent>
            </LazyTabContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="hr">
              <TabsContent value="hr">
                <HRTab 
                  user={user} 
                  selectedProjectId={selectedProjectId} 
                  selectedProjectName={selectedProjectName} 
                  projectRequirements={projectRequirements} 
                  organizationStructures={organizationStructures} 
                  nodes={nodes} 
                  roleJds={roleJds} 
                  candidates={candidates} 
                  setSelectedProjectId={setSelectedProjectId}
                />
              </TabsContent>
            </LazyTabContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="sales">
              <TabsContent value="sales">
                <SalesTab user={user} />
              </TabsContent>
            </LazyTabContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="marketing">
              <TabsContent value="marketing">
                <MarketingTab user={user} />
              </TabsContent>
            </LazyTabContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="finance">
              <TabsContent value="finance">
                <FinanceTab user={user} />
              </TabsContent>
            </LazyTabContent>
            
            <LazyTabContent activeTab={activeTab} tabValue="legal">
              <TabsContent value="legal">
                <LegalTab user={user} />
              </TabsContent>
            </LazyTabContent>
          </Tabs>
        </div>
      </main>
      
      <ContactIngantiSupportDialog 
        open={showContactDialog} 
        onOpenChange={setShowContactDialog} 
      />
    </div>
  );
};

export default Administration;
